import { useEffect, useState, useRef } from "react";
import { supabase } from "../lib/supabase";
import { useUser } from "../context/UserContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faUser, faPaperPlane } from "@fortawesome/free-solid-svg-icons";
import "../styles/sharePostModal.css";

function SharePostModal({ open, onClose, post }) {
    const { user } = useUser();

    const [conversations, setConversations] = useState([]);
    const [search, setSearch] = useState("");
    const [filtered, setFiltered] = useState([]);
    const [selected, setSelected] = useState(null);
    const [messageText, setMessageText] = useState("");
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);

    const inputRef = useRef(null);

    // ── Fetch conversations on open ────────────────────────────────────────────
    useEffect(() => {
        if (!open || !user) return;
        setSearch("");
        setSelected(null);
        setMessageText("");
        setSent(false);

        const fetchConvs = async () => {
            const { data } = await supabase
                .from("conversations")
                .select(`
                    *,
                    user1:profiles!conversations_user1_id_fkey(id, username, full_name, avatar_url),
                    user2:profiles!conversations_user2_id_fkey(id, username, full_name, avatar_url)
                `)
                .or(`user1_id.eq.${user.id},user2_id.eq.${user.id}`);

            if (!data) return;

            const formatted = data.map(conv => ({
                ...conv,
                otherUser: conv.user1_id === user.id ? conv.user2 : conv.user1,
            }));

            setConversations(formatted);
            setFiltered(formatted);
        };

        fetchConvs();
        setTimeout(() => inputRef.current?.focus(), 100);
    }, [open, user]);

    // ── Search filter ──────────────────────────────────────────────────────────
    useEffect(() => {
        if (!search.trim()) { setFiltered(conversations); return; }
        const q = search.toLowerCase();
        setFiltered(conversations.filter(c =>
            c.otherUser?.username?.toLowerCase().includes(q) ||
            c.otherUser?.full_name?.toLowerCase().includes(q)
        ));
    }, [search, conversations]);

    // ── Send ───────────────────────────────────────────────────────────────────
    const handleSend = async () => {
        if (!selected || sending) return;
        setSending(true);

        const content = messageText.trim() || "Sent a post";

        await supabase.from("messages").insert({
            conversation_id: selected.id,
            sender_id: user.id,
            content,
            message_type: "post",
            post_id: post.id,
        });

        setSending(false);
        setSent(true);
        setTimeout(() => {
            setSent(false);
            onClose();
        }, 1200);
    };

    if (!open) return null;

    // Post thumbnail for preview
    const thumbUrl = post?.post_images?.[0]?.signedUrl;

    return (
        <div className="spm-overlay" onClick={onClose}>
            <div className="spm-modal" onClick={e => e.stopPropagation()}>

                {/* HEADER */}
                <div className="spm-header">
                    <span className="spm-title">Share</span>
                    <button className="spm-close" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>
                <div className="spm-divider" />

                {/* SEARCH */}
                <div className="spm-search-row">
                    <span className="spm-to">To:</span>
                    <input
                        ref={inputRef}
                        className="spm-search-input"
                        placeholder="Search..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                </div>
                <div className="spm-divider" />

                {/* SUGGESTED LIST */}
                <div className="spm-list">
                    {filtered.length === 0 ? (
                        <p className="spm-empty">No conversations yet</p>
                    ) : (
                        <>
                            <p className="spm-section-label">Suggested</p>
                            {filtered.map(conv => (
                                <div
                                    key={conv.id}
                                    className={`spm-user-row ${selected?.id === conv.id ? "selected" : ""}`}
                                    onClick={() => setSelected(selected?.id === conv.id ? null : conv)}
                                >
                                    {conv.otherUser?.avatar_url ? (
                                        <img src={conv.otherUser.avatar_url} className="spm-avatar" alt="" />
                                    ) : (
                                        <div className="spm-avatar spm-avatar-fallback">
                                            <FontAwesomeIcon icon={faUser} />
                                        </div>
                                    )}
                                    <div className="spm-user-info">
                                        <span className="spm-username">{conv.otherUser?.username}</span>
                                        {conv.otherUser?.full_name && (
                                            <span className="spm-fullname">{conv.otherUser.full_name}</span>
                                        )}
                                    </div>
                                    {/* Checkmark */}
                                    <div className={`spm-check ${selected?.id === conv.id ? "checked" : ""}`} />
                                </div>
                            ))}
                        </>
                    )}
                </div>

                {/* MESSAGE + SEND — only when someone is selected */}
                {selected && (
                    <>
                        <div className="spm-divider" />
                        <div className="spm-compose">
                            {/* Post thumbnail preview */}
                            {thumbUrl && (
                                <div className="spm-post-preview">
                                    <img src={thumbUrl} alt="" className="spm-post-thumb" />
                                    <span className="spm-post-author">@{post.profiles?.username || "unknown"}</span>
                                </div>
                            )}

                            <div className="spm-message-row">
                                <input
                                    className="spm-message-input"
                                    placeholder="Write a message..."
                                    value={messageText}
                                    onChange={e => setMessageText(e.target.value)}
                                    onKeyDown={e => { if (e.key === "Enter") handleSend(); }}
                                />
                                <button
                                    className={`spm-send-btn ${sent ? "sent" : ""}`}
                                    onClick={handleSend}
                                    disabled={sending || sent}
                                >
                                    {sent ? "Sent!" : sending ? "..." : (
                                        <FontAwesomeIcon icon={faPaperPlane} />
                                    )}
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

export default SharePostModal;
